import useSwipeToClose from '../../hooks/useSwipeToClose';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Spinner from '../UI/Spinner';
import { detectEmailProvider, openEmailClient } from '../../utils/emailProviders';
import backendClient from '../../services/backendClient';
import { 
  trackAuthStarted, 
  trackAuthCompleted, 
  setUserId,
  AUTH_INTENT, 
  AUTH_METHOD 
} from '../../analytics';

// Email validation helper
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Common email domain typos and their corrections
const DOMAIN_CORRECTIONS = {
  // Gmail
  'gmail.com': ['gmal.com', 'gmial.com', 'gmaill.com', 'gmali.com', 'gmil.com', 'gmai.com', 'gamil.com', 'gnail.com', 'gmail.co', 'gmail.cm', 'gmail.om', 'gmail.con', 'gmail.cpm', 'gmail.vom', 'gmail.xom', 'gmaik.com', 'gmaol.com', 'gmaikl.com', 'gmaiil.com', 'gmaail.com', 'ggmail.com', 'fmail.com', 'hmail.com', 'vmail.com', 'gimail.com', 'gemail.com', 'gmsil.com', 'gmqil.com', 'gmzil.com', 'gmwil.com'],
  
  // Hotmail
  'hotmail.com': ['hotmal.com', 'hotmai.com', 'hotmial.com', 'hotmali.com', 'hotmil.com', 'hotmaill.com', 'hotmial.com', 'hotamil.com', 'hotmral.com', 'hotmaol.com', 'hotmaik.com', 'hotmail.co', 'hotmail.cm', 'hotmail.om', 'hotmail.con', 'hotmail.cpm', 'hotmail.vom', 'hhotmail.com', 'hotmmail.com', 'hotmaail.com', 'hotmaiil.com', 'homail.com', 'htmail.com', 'hptmail.com', 'hotmsil.com', 'hotmqil.com', 'hitmail.com', 'jotmail.com', 'gotmail.com', 'hormail.com', 'hoymail.com'],
  
  // Outlook
  'outlook.com': ['outllok.com', 'outlok.com', 'outloo.com', 'outlookk.com', 'outloook.com', 'outlook.co', 'outlook.cm', 'outlook.om', 'outlook.con', 'outook.com', 'outlool.com', 'outlooi.com', 'outloom.com', 'oultook.com', 'putlook.com', 'iutlook.com', 'oulook.com', 'ourlook.com', 'ouylook.com', 'outliok.com', 'outlpok.com', 'outl0ok.com', 'outl9ok.com'],
  
  // Yahoo
  'yahoo.com': ['yaho.com', 'yahooo.com', 'yaho.com', 'yaoo.com', 'yhoo.com', 'yahho.com', 'yahooi.com', 'yahoo.co', 'yahoo.cm', 'yahoo.om', 'yahoo.con', 'yahoo.cpm', 'yhaoo.com', 'yahooo.com', 'uahoo.com', 'tahoo.com', 'yahpp.com', 'yahii.com', 'yah0o.com', 'yah9o.com', 'yahoi.com', 'yahop.com', 'yahoio.com'],
  
  // Live
  'live.com': ['liv.com', 'livee.com', 'live.co', 'live.cm', 'live.om', 'live.con', 'lve.com', 'ive.com', 'liive.com', 'livve.com', 'kive.com', 'oive.com', 'luve.com', 'love.com', 'l8ve.com', 'l9ve.com', 'lice.com', 'libe.com', 'life.com'],
  
  // iCloud
  'icloud.com': ['icoud.com', 'iclod.com', 'iclould.com', 'icloud.co', 'icloud.cm', 'icloud.om', 'icloud.con', 'iclooud.com', 'iclouud.com', 'icloudd.com', 'iclud.com', 'icluod.com', 'icloid.com', 'iclpud.com', 'icl0ud.com', 'icl9ud.com', 'ucloud.com', 'ocloud.com', 'icliud.com'],
  
  // Protonmail
  'protonmail.com': ['protonmal.com', 'protonmai.com', 'protonmial.com', 'protonmail.co', 'protonmail.cm', 'protonmail.con', 'protonmeil.com', 'protonmaill.com', 'protonmil.com', 'protnmail.com', 'protomail.com', 'protonmaik.com', 'proronmail.com', 'proyonmail.com', 'ptotonmail.com', 'prptonmail.com', 'pritonmail.com', 'pr0tonmail.com'],
  'proton.me': ['proton.m', 'proton.mee', 'proton.mr', 'proton.ne', 'proton.md', 'prton.me', 'protn.me', 'protom.me', 'proron.me', 'proyon.me'],
  
  // MSN
  'msn.com': ['msn.co', 'msn.cm', 'msn.om', 'msn.con', 'mns.com', 'msnn.com', 'mssn.com', 'nsn.com', 'msm.com'],
  
  // AOL
  'aol.com': ['aol.co', 'aol.cm', 'aol.om', 'aol.con', 'aoll.com', 'ao.com', 'aool.com', 'aaol.com', 'aok.com', 'aoi.com', 'sol.com', 'qol.com', 'zol.com'],
  
  // Ymail
  'ymail.com': ['ymal.com', 'ymial.com', 'ymail.co', 'ymail.cm', 'ymail.con', 'ymaill.com', 'ymali.com', 'ymil.com', 'umail.com', 'tmail.com'],
  
  // Me (Apple)
  'me.com': ['me.co', 'me.cm', 'me.om', 'me.con', 'mee.com', 'mr.com', 'ne.com', 'md.com'],
  
  // Mac (Apple)
  'mac.com': ['mac.co', 'mac.cm', 'mac.om', 'mac.con', 'macc.com', 'maac.com', 'msc.com', 'mzc.com', 'nac.com'],

  // GMX
  'gmx.com': ['gmx.co', 'gmx.cm', 'gmx.om', 'gmx.con', 'gmxx.com', 'gmmx.com', 'gnx.com', 'gmc.com', 'gmz.com'],

  // Zoho
  'zoho.com': ['zoho.co', 'zoho.cm', 'zoho.om', 'zoho.con', 'zohoo.com', 'zoh.com', 'zooh.com', 'xoho.com', 'ziho.com', 'zpho.com'],

  // Fastmail
  'fastmail.com': ['fastmal.com', 'fastmai.com', 'fastmail.co', 'fastmail.cm', 'fastmail.con', 'fasmail.com', 'fastmial.com', 'fastmaill.com', 'fastmsil.com', 'fsatmail.com', 'fastemail.com'],

  // Yandex
  'yandex.com': ['yandex.co', 'yandex.cm', 'yandex.con', 'yadex.com', 'yandez.com', 'yanex.com', 'yandx.com', 'yandxe.com', 'uandex.com', 'tandex.com'],
  'yandex.ru': ['yandex.r', 'yandex.ruu', 'yandex.tu', 'yandex.ry', 'yadex.ru', 'yandez.ru'],

  // Common TLD mistakes for any domain
};

// Build reverse lookup map for faster correction
const buildTypoMap = () => {
  const map = {};
  for (const [correct, typos] of Object.entries(DOMAIN_CORRECTIONS)) {
    for (const typo of typos) {
      map[typo.toLowerCase()] = correct;
    }
  }
  return map;
};

const TYPO_MAP = buildTypoMap();

// Levenshtein distance for fuzzy matching
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
};

// Correct email domain typos
const correctEmailDomain = (email) => {
  if (!email || !email.includes('@')) return email;
  
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  
  const lowerDomain = domain.toLowerCase();
  
  // Check exact match in typo map
  if (TYPO_MAP[lowerDomain]) {
    return `${localPart}@${TYPO_MAP[lowerDomain]}`;
  }
  
  // Check if domain is already correct
  if (DOMAIN_CORRECTIONS[lowerDomain]) {
    return email;
  }
  
  // Fuzzy match - find closest domain if within 2 edits
  const correctDomains = Object.keys(DOMAIN_CORRECTIONS);
  let bestMatch = null;
  let bestDistance = 3; // Max 2 character differences
  
  for (const correctDomain of correctDomains) {
    const distance = levenshteinDistance(lowerDomain, correctDomain);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestMatch = correctDomain;
    }
  }
  
  if (bestMatch) {
    return `${localPart}@${bestMatch}`;
  }
  
  return email;
};

// Floating Label Input Component
const FloatingInput = ({ 
  icon, 
  label, 
  value, 
  onChange, 
  onKeyPress,
  onBlur,
  type = 'text', 
  error = '',
  rightElement,
  autoFocus = false,
  autoComplete,
  name,
  id
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;
  const isActive = isFocused || hasValue;
  const hasError = error && error.length > 0;

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) onBlur();
  };

  return (
    <div className="w-full">
      <div 
        className={`
          h-[56px] rounded-[12px] relative transition-all duration-200
          ${isActive ? 'bg-white' : 'bg-[#fff] md:hover:bg-white'}
        `}
      >
        {/* Border overlay */}
        <div 
          aria-hidden="true" 
          className={`absolute inset-0 pointer-events-none rounded-[12px] transition-all duration-200 ${
            hasError 
              ? 'border-[1.5px] border-[#b50b0b]' 
              : isFocused 
                ? 'border-[1.5px] border-[#0d0c07]' 
                : 'border-[0.5px] border-[#56554d]'
          }`}
        />
        
        <div className="flex items-center h-full px-3 gap-2">
          {/* Icon */}
          {icon && (
            <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
              {icon}
            </div>
          )}
          
          {/* Input area */}
          <div className="flex-1 relative h-full flex flex-col justify-center">
            {/* Floating label */}
            <span 
              className={`
                absolute left-0 pointer-events-none
                transition-[top,font-size,line-height,color] duration-200
                ${hasError ? 'text-[#b50b0b]' : 'text-[#72726e]'}
                ${isActive 
                  ? 'top-[8px] text-[10.8px] leading-[14px]' 
                  : 'top-[16px] text-[16px] leading-[24px]'
                }
              `}
            >
              {label}
            </span>
            
            <input
              type={type}
              value={value || ''}
              onChange={onChange}
              onKeyPress={onKeyPress}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              autoFocus={autoFocus}
              autoComplete={autoComplete}
              name={name}
              id={id}
              className={`
                w-full bg-transparent text-[16px] text-[#0d0c07]
                outline-none border-none focus:outline-none focus:ring-0
                leading-[24px] transition-all duration-200
                ${isActive ? 'pt-[14px] font-semibold' : 'font-normal pt-0'}
              `}
              style={{ 
                boxShadow: 'none',
                WebkitBoxShadow: '0 0 0 1000px white inset',
                WebkitTextFillColor: '#0d0c07'
              }}
            />
          </div>

          {/* Right element (button) */}
          {rightElement && (
            <div className="flex-shrink-0">
              {rightElement}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LoginModal = ({ isOpen, onClose, onLoginSuccess, context = 'save' }) => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState(''); // 'error' or 'info'
  const [isNewUser, setIsNewUser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [hasTrackedOpen, setHasTrackedOpen] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  const { containerRef, dragOffset, isAnimating, shouldRender, isVisible, DragHandle } = useSwipeToClose(onClose, isOpen);

  // Reset form state when modal is re-opened so the user always starts at step 1
  // (email input), never at step 2 (OTP input) from a previous open session.
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setEmail('');
      setOtp('');
      setErrorMessage('');
      setErrorType('');
    }
  }, [isOpen]);

  // Track Auth Started when modal opens
  useEffect(() => {
    if (isOpen && !hasTrackedOpen) {
      trackAuthStarted({
        intent: context === 'save' ? AUTH_INTENT.SAVE_PLAN : AUTH_INTENT.LOGIN,
        methods_shown: [AUTH_METHOD.EMAIL, AUTH_METHOD.GOOGLE, AUTH_METHOD.APPLE],
        default_method: AUTH_METHOD.EMAIL,
      });
      setHasTrackedOpen(true);
    }
    if (!isOpen) {
      setHasTrackedOpen(false);
    }
  }, [isOpen, hasTrackedOpen, context]);

  // Start countdown when entering step 2
  useEffect(() => {
    if (step === 2) {
      setResendCountdown(59);
    }
  }, [step]);

  // Countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Get email provider for dynamic button text
  const emailProvider = detectEmailProvider(email);

  // Handle email link click in messages
  const handleEmailLinkClick = useCallback(() => {
    if (emailProvider) {
      openEmailClient(email);
    }
  }, [email, emailProvider]);

  // Validate email on blur and auto-correct domain typos
  const validateEmailOnBlur = () => {
    if (!email.trim()) {
      setErrorMessage(t('errors.enterEmail'));
      setErrorType('error');
      return;
    }
    
    // Try to correct domain typos first
    const correctedEmail = correctEmailDomain(email.trim());
    if (correctedEmail !== email.trim()) {
      setEmail(correctedEmail);
    }
    
    if (!isValidEmail(correctedEmail)) {
      setErrorMessage(t('errors.invalidEmail'));
      setErrorType('error');
      return;
    }
    
    // Clear error if valid
    setErrorMessage('');
    setErrorType('');
  };

  // Validate and proceed with email
  const handleEmailSubmit = async () => {
    // Try to correct domain typos first
    const correctedEmail = correctEmailDomain(email.trim());
    if (correctedEmail !== email.trim()) {
      setEmail(correctedEmail);
    }
    
    // Validate email
    if (!correctedEmail) {
      setErrorMessage(t('errors.enterEmail'));
      setErrorType('error');
      return;
    }
    
    if (!isValidEmail(correctedEmail)) {
      setErrorMessage(t('errors.invalidEmail'));
      setErrorType('error');
      return;
    }

    setErrorMessage('');
    setErrorType('');
    setIsLoading(true);
    
    try {
      const data = await backendClient.post('/check-user', { email }, { timeout: 10000 });
      
      if (data.exists) {
        const otpData = await backendClient.post('/send-otp', { email }, { timeout: 10000 });
        
        goToStep2();
        setErrorMessage('');
        setErrorType('');
        
        // Show info message if OTP was already sent
        if (otpData.message && otpData.message.includes('already sent')) {
          setErrorMessage('already_sent');
          setErrorType('info');
        }
      } else {
        // New user - create account and send OTP for verification
        const createData = await backendClient.post('/create-user', { email, marketingConsent }, { timeout: 10000 });
        
        if (createData.requiresVerification) {
          // New user needs to verify email with OTP
          setIsNewUser(true);
          goToStep2();
          setErrorMessage('');
          setErrorType('');
        } else if (createData.token) {
          // Fallback for backwards compatibility (shouldn't happen with new backend)
          localStorage.setItem('authToken', createData.token);
          localStorage.setItem('email', email);
          window.dispatchEvent(new Event('authTokenChanged'));
          setIsNewUser(true);
          
          // Track successful signup
          trackAuthCompleted({
            method: AUTH_METHOD.EMAIL,
            is_new_user: true,
            user_id: createData.userId || email,
            is_guest_before: true,
          });
          
          // Set user ID in Amplitude
          if (createData.userId) {
            setUserId(createData.userId);
          }
          
          onLoginSuccess(createData.token);
          onClose();
          // Only reload if not in 'save' or 'premium' context - let parent handle navigation
          if (context !== 'save' && context !== 'premium') {
            window.location.reload();
          }
        } else {
          setErrorMessage(t('errors.createFailed'));
          setErrorType('error');
        }
      }
    } catch (error) {
      console.error('Error in login flow:', error);
      setErrorMessage(error.data?.error || error.message || t('errors.generic'));
      setErrorType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async () => {
    if (!otp.trim()) return;
    
    setIsLoading(true);
    setErrorMessage('');
    setErrorType('');
    
    try {
      const data = await backendClient.post('/verify-otp', { email, otp }, { timeout: 10000 });

      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('email', email);
        window.dispatchEvent(new Event('authTokenChanged'));
        
        // Track successful login
        trackAuthCompleted({
          method: AUTH_METHOD.EMAIL,
          is_new_user: false,
          user_id: data.userId || email,
          is_guest_before: true,
        });
        
        // Set user ID in Amplitude
        if (data.userId) {
          setUserId(data.userId);
        }
        
        onLoginSuccess(data.token);
        onClose();
        // Only reload if not in 'save' or 'premium' context - let parent handle navigation
        if (context !== 'save' && context !== 'premium') {
          window.location.reload();
        }
      } else {
        setErrorMessage('invalid_otp');
        setErrorType('error');
      }
    } catch (error) {
      setErrorMessage('invalid_otp');
      setErrorType('error');
      console.error('Error verifying OTP:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    
    setIsLoading(true);
    try {
      await backendClient.post('/send-otp', { email }, { timeout: 10000 });
      setResendCountdown(59);
      setErrorMessage('');
      setErrorType('');
    } catch (error) {
      console.error('Error resending OTP:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Go back to step 1
  const handleBack = () => {
    setStep(1);
    setOtp('');
    setErrorMessage('');
    setErrorType('');
    setResendCountdown(0);
  };

  // Go to step 2
  const goToStep2 = () => {
    setStep(2);
  };

  if (!shouldRender) return null;

  // Check if email is valid for green button
  const isEmailValid = isValidEmail(email);

  // Icons
  const MailIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
      <path d="M18.333 5.833L10.858 10.583C10.601 10.744 10.303 10.83 10 10.83C9.696 10.83 9.399 10.744 9.142 10.583L1.667 5.833M3.333 3.333H16.667C17.587 3.333 18.333 4.079 18.333 5V15C18.333 15.92 17.587 16.667 16.667 16.667H3.333C2.413 16.667 1.667 15.92 1.667 15V5C1.667 4.079 2.413 3.333 3.333 3.333Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ArrowRightIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ArrowLeftIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CheckIcon = (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
      <path d="M16.666 5L7.5 14.167L3.333 10" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const SparklesIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clip-path="url(#clip0_2383_19934)">
        <path d="M16.6667 2.49956V5.83289M18.3334 4.16622H15M3.33336 14.1662V15.8329M4.1667 14.9996H2.50003M8.28086 12.9162C8.20647 12.6278 8.05615 12.3646 7.84555 12.154C7.63494 11.9434 7.37176 11.7931 7.08336 11.7187L1.97086 10.4004C1.88364 10.3756 1.80687 10.3231 1.75221 10.2508C1.69754 10.1784 1.66797 10.0902 1.66797 9.99956C1.66797 9.90889 1.69754 9.82069 1.75221 9.74835C1.80687 9.67601 1.88364 9.62348 1.97086 9.59872L7.08336 8.27956C7.37166 8.20523 7.63477 8.05503 7.84537 7.84459C8.05596 7.63414 8.20634 7.37113 8.28086 7.08289L9.5992 1.97039C9.6237 1.88282 9.67618 1.80567 9.74863 1.75072C9.82108 1.69576 9.90951 1.66602 10.0004 1.66602C10.0914 1.66602 10.1798 1.69576 10.2523 1.75072C10.3247 1.80567 10.3772 1.88282 10.4017 1.97039L11.7192 7.08289C11.7936 7.37128 11.9439 7.63447 12.1545 7.84507C12.3651 8.05567 12.6283 8.20599 12.9167 8.28039L18.0292 9.59789C18.1171 9.62214 18.1946 9.67456 18.2499 9.74712C18.3052 9.81968 18.3351 9.90836 18.3351 9.99956C18.3351 10.0908 18.3052 10.1794 18.2499 10.252C18.1946 10.3245 18.1171 10.377 18.0292 10.4012L12.9167 11.7187C12.6283 11.7931 12.3651 11.9434 12.1545 12.154C11.9439 12.3646 11.7936 12.6278 11.7192 12.9162L10.4009 18.0287C10.3764 18.1163 10.3239 18.1934 10.2514 18.2484C10.179 18.3034 10.0905 18.3331 9.99961 18.3331C9.90868 18.3331 9.82025 18.3034 9.7478 18.2484C9.67535 18.1934 9.62287 18.1163 9.59836 18.0287L8.28086 12.9162Z" stroke="#006600" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <defs>
        <clipPath id="clip0_2383_19934">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );

  return createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] bg-opacity-50 z-50 drawer-overlay transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
      onClick={onClose}
    >
      <div 
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[98vh] overflow-hidden"
        style={{
          transform: isVisible ? `translateY(${Math.max(0, dragOffset)}px)` : 'translateY(100%)',
          transition: isAnimating ? 'transform 0.3s ease-out' : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-1 right-1 p-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="overflow-x-hidden overflow-y-auto flex-1">
          {/* Sliding container */}
          <div 
            className="flex transition-transform duration-300 ease-out"
            style={{
              transform: step === 1 ? 'translateX(0%)' : 'translateX(-100%)'
            }}
          >
            {/* Step 1 Panel */}
            <div className="w-full flex-shrink-0">
              <div className="box-border flex flex-col items-start pb-16 pt-8 px-6">
                {/* Title */}
                <div className="w-full pb-6">
                  <h2 className="font-bold text-[24px] leading-[40px] text-[#0d0c07]">
                    {context === 'signin' ? t('signIn.title') : t('saveMeals.title')}
                  </h2>
                  <p className="text-[15.2px] leading-[28px] text-[#65635d]">
                    
                  </p>

                  {/* Credits benefit */}
                  <div className="flex gap-1 items-start">
                    <p className="font-normal text-[15.2px] leading-[28px] text-[#005500] ">
                      {context === 'signin' ? t('signIn.subtitle') : t('saveMeals.subtitle')} 
                    </p>
                  </div>
                </div>

                {/* Error Message for Step 1 */}
                {errorMessage && errorType === 'error' && step === 1 && (
                  <div className="w-full pb-5">
                    <div className="bg-[rgba(181,11,11,0.08)] rounded-[12px] px-4 py-3">
                      <p className="text-[13.2px] leading-[20px] text-[#b50b0b]">
                        {errorMessage}
                      </p>
                    </div>
                  </div>
                )}

                {/* Selectors */}
                <div className="w-full flex flex-col gap-11">
                  
                  {/* Email Input Section */}
                  <div className="flex flex-col gap-8">
                    
                    {/* Email Input */}
                    <div className="w-full">
                      <FloatingInput
                        icon={MailIcon}
                        label={t('email')}
                        type="email"
                        name="email"
                        id="login-email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errorMessage) {
                            setErrorMessage('');
                            setErrorType('');
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleEmailSubmit();
                          }
                        }}
                        onBlur={validateEmailOnBlur}
                        error={errorMessage && errorType === 'error' ? errorMessage : ''}
                        rightElement={
                          <button
                            onClick={handleEmailSubmit}
                            disabled={isLoading}
                            className={`
                              h-9 w-9 flex items-center justify-center
                              rounded-[6px] transition-colors
                              ${isEmailValid 
                                ? 'bg-[#006600] md:hover:bg-[#005500]' 
                                : 'bg-[#0d0c07] md:hover:bg-[#353227]'
                              }
                              disabled:opacity-50 disabled:cursor-not-allowed
                            `}
                          >
                            {isLoading ? (
                              <Spinner size="sm" color="#FFFFFF" />
                            ) : (
                              ArrowRightIcon
                            )}
                          </button>
                        }
                      />
                    </div>

                    {/* Marketing consent checkbox */}
                    <label className="flex items-center gap-2.5 cursor-pointer -mt-2" onClick={() => setMarketingConsent(!marketingConsent)}>
                      <div className="flex-shrink-0">
                        <div
                          className={`w-6 h-6 rounded-[5px] border flex items-center justify-center transition-colors ${
                            marketingConsent
                              ? 'bg-[#006600] border-[#006600]'
                              : 'bg-white border-[#c1c0be]'
                          }`}
                        >
                          {marketingConsent && (
                            <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                              <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-[14px] leading-[18px] text-[#65635D]">
                        {t('marketingConsent')}
                      </span>
                    </label>

                    {/* OR Divider */}
                    <div className="flex items-center h-6 px-8 w-full">
                      <div className="flex-1 h-[0.5px] bg-[#c1c0be]"></div>
                      <div className="px-3 py-0 pb-1 border-[0.5px] border-[#c1c0be] rounded-[12px]">
                        <span className="text-[12px] leading-[20px] text-[#878683]">{t('orContinueVia')}</span>
                      </div>
                      <div className="flex-1 h-[0.5px] bg-[#c1c0be]"></div>
                    </div>

                    {/* Social Login Buttons */}
                    <div className="flex gap-3 w-full">
                      {/* Google Button */}
                      <button 
                        onClick={() => {
                          // OAuth requires full page redirect (can't use fetch)
                          const { API_BASE_URL } = require('../../services/backendClient');
                          const { openExternalUrl, getPlatform } = require('../../utils/capacitorBridge');
                          // Store return path so App.js can navigate back after OAuth callback
                          // For premium context (checkout flow), always return to /premium
                          if (context === 'premium') {
                            localStorage.setItem('oauthReturnPath', '/premium');
                            localStorage.setItem('oauthPendingCheckout', '1');
                          } else {
                            localStorage.setItem('oauthReturnPath', window.location.pathname);
                          }
                          const platform = getPlatform(); // 'ios', 'android', or 'web'
                          openExternalUrl(`${API_BASE_URL}/google?platform=${platform}`);
                        }}
                        className="flex-1 h-12 bg-[#e2e1df] rounded-[12px] border-[0.5px] border-[#e2e1df] md:hover:bg-[#D7D7D5] transition-colors flex items-center justify-center gap-[6px]"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M15.1434 6.95437L18.383 4.30219C16.6511 2.8643 14.4264 2 12 2C8.13516 2 4.78242 4.1923 3.11812 7.40109C2.40328 8.77801 2 10.3418 2 12C2 13.7148 2.43133 15.3286 3.19176 16.739C4.88078 19.8714 8.19176 22 12 22C14.3698 22 16.5467 21.1758 18.2604 19.7984C20.0225 18.3819 21.2945 16.3802 21.7813 14.0879C21.9247 13.4143 22 12.7159 22 12C22 11.3615 21.9401 10.7368 21.8253 10.1319H17.6451H12.2198V14.0879H17.5671C17.0858 15.3725 16.172 16.4456 15.0028 17.1313C14.122 17.6484 13.0956 17.9445 12 17.9445C9.45109 17.9445 7.27691 16.3401 6.43242 14.0863C6.18848 13.4374 6.05551 12.7341 6.05543 12C6.05543 11.3236 6.16863 10.6736 6.37633 10.0681C7.17855 7.73348 9.3934 6.05547 12 6.05547C13.1544 6.05547 14.2319 6.38461 15.1434 6.95437Z" fill="#0D0C07"/>
                        </svg>
                        <span className="text-[14px] font-semibold text-[#0d0c07] leading-[24px]">Google</span>
                      </button>

                      {/* Apple Button */}
                      <button
                        onClick={() => {
                          // OAuth requires full page redirect (can't use fetch)
                          const { API_BASE_URL } = require('../../services/backendClient');
                          const { openExternalUrl, getPlatform } = require('../../utils/capacitorBridge');
                          // Store return path so App.js can navigate back after OAuth callback
                          // For premium context (checkout flow), always return to /premium
                          if (context === 'premium') {
                            localStorage.setItem('oauthReturnPath', '/premium');
                            localStorage.setItem('oauthPendingCheckout', '1');
                          } else {
                            localStorage.setItem('oauthReturnPath', window.location.pathname);
                          }
                          const platform = getPlatform(); // 'ios', 'android', or 'web'
                          openExternalUrl(`${API_BASE_URL}/apple?platform=${platform}`);
                        }}
                        className="flex-1 h-12 bg-[#e2e1df] rounded-[12px] border-[0.5px] border-[#e2e1df] md:hover:bg-[#D7D7D5] transition-colors flex items-center justify-center gap-[6px]"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.4196 12.6407C16.3986 10.3095 18.3344 9.19656 18.4234 9.14389C17.3448 7.53495 15.6566 7.31627 15.0685 7.29865C13.67 7.14959 12.3144 8.15076 11.6029 8.15076C10.8783 8.15076 9.78375 7.31344 8.60147 7.33743C7.07865 7.36142 5.65377 8.25877 4.86963 9.65667C3.25289 12.4477 4.45852 16.5517 6.00771 18.8297C6.78694 19.9453 7.69757 21.1905 8.88906 21.1465C10.0558 21.0984 10.4912 20.3909 11.8992 20.3909C13.2943 20.3909 13.7034 21.1465 14.9194 21.1184C16.1701 21.0984 16.9582 20.0006 17.7094 18.8751C18.6095 17.5961 18.9721 16.3357 18.9868 16.2726C18.9575 16.2626 16.4439 15.2915 16.4196 12.6407Z" fill="#0D0C07"/>
                          <path d="M14.154 5.76141C14.7808 4.97389 15.2124 3.90387 15.0936 2.8125C14.1833 2.85261 13.0445 3.4492 12.3926 4.22068C11.8166 4.90085 11.3025 6.01246 11.4359 7.06242C12.4604 7.13663 13.5011 6.53353 14.154 5.76141Z" fill="#0D0C07"/>
                        </svg>
                        <span className="text-[14px] font-semibold text-[#0d0c07] leading-[24px]">{t('socialButtons.apple')}</span>
                      </button>
                    </div>
                  </div>

                  {/* Benefits Section 
                  <div className="flex flex-col gap-3">
                    

                    {/* Personalisations benefit 
                    <div className="flex gap-2 items-center">
                      <div className="w-7 h-7 bg-white rounded-[6px] flex items-center justify-center">
                        {CheckIcon}
                      </div>
                      <p className="text-[15.2px] leading-[28px] text-[#0d0c07]">
                        {t('benefits.saveMeals')}
                      </p>
                    </div>

                    {/* Notifications benefit 
                    <div className="flex gap-2 items-center">
                      <div className="w-7 h-7 bg-white rounded-[6px] flex items-center justify-center">
                        {CheckIcon}
                      </div>
                      <p className="text-[15.2px] leading-[28px] text-[#0d0c07]">
                        {t('benefits.reminders')}
                      </p>
                    </div>
                  </div>*/}
                </div>
              </div>
            </div>

            {/* Step 2 Panel */}
            <div className="w-full flex-shrink-0">
              <div className="box-border flex flex-col items-start pb-16 pt-8 px-6">
                {/* Back Button - only show for existing users who can go back */}
                {!isNewUser && (
                  <div className="pb-3">
                    <button
                      onClick={handleBack}
                      className="w-9 h-9 rounded-[12px] border-[0.5px] border-[#0d0c07] flex items-center justify-center text-[#0D0C07] md:hover:bg-[#0D0C07] md:hover:text-white transition-colors"
                    >
                      {ArrowLeftIcon}
                    </button>
                  </div>
                )}

                {/* Title */}
                <div className="w-full pb-6">
                  <h2 className="font-bold text-[24px] leading-[40px] text-[#0d0c07]">
                    {t('verification.title')}
                  </h2>
                  <p className="text-[15.2px] leading-[28px] text-[#65635d]">
                    {t('verification.subtitle')}
                  </p>
                </div>

                    {/* Error/Info Messages */}
                    {errorMessage && (
                      <div className="w-full pb-5">
                        {errorType === 'error' && errorMessage === 'invalid_otp' && (
                          <div className="bg-[rgba(181,11,11,0.08)] rounded-[12px] px-4 py-3 w-full">
                            <p className="text-[13.2px] leading-[20px] text-[#b50b0b]">
                              {t('verification.invalidCode')}{' '}
                              <button 
                                onClick={handleEmailLinkClick}
                                className="underline decoration-solid md:hover:opacity-70 transition-opacity"
                              >
                                {t('verification.checkEmail')}
                              </button>.
                            </p>
                          </div>
                        )}
                        {errorType === 'info' && errorMessage === 'already_sent' && (
                          <div className="bg-[rgba(0,102,0,0.08)] rounded-[12px] px-4 py-3 w-full">
                            <p className="text-[12.8px] leading-[20px] text-[#006600]">
                              {t('verification.codeStillActive')}{' '}
                              <button 
                                onClick={handleEmailLinkClick}
                                className="underline decoration-solid md:hover:opacity-70 transition-opacity"
                              >
                                {t('verification.checkEmail')}
                              </button>.
                            </p>
                          </div>
                        )}
                        {errorType === 'error' && errorMessage !== 'invalid_otp' && (
                          <div className="bg-[rgba(181,11,11,0.08)] rounded-[12px] px-4 py-3 w-full">
                            <p className="text-[13.2px] leading-[20px] text-[#b50b0b]">
                              {errorMessage}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Selectors */}
                    <div className="w-full flex flex-col gap-11">
                      
                      {/* OTP Input Section */}
                      <div className="flex flex-col gap-4">
                        
                        {/* OTP Input */}
                        <div className="w-full">
                          <FloatingInput
                            label={t('verification.enterCode')}
                            value={otp}
                            onChange={(e) => {
                              setOtp(e.target.value);
                              if (errorMessage) {
                                setErrorMessage('');
                                setErrorType('');
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && otp.trim()) {
                                verifyOTP();
                              }
                            }}
                            autoFocus={step === 2}
                            rightElement={
                              <button
                                onClick={verifyOTP}
                                disabled={isLoading || !otp.trim()}
                                className="
                                  h-9 w-9 flex items-center justify-center
                                  rounded-[6px] bg-[#0d0c07] md:hover:bg-[#353227]
                                  transition-colors
                                  disabled:opacity-50 disabled:cursor-not-allowed
                                "
                              >
                                {isLoading ? (
                                  <Spinner size="sm" color="#FFFFFF" />
                                ) : (
                                  ArrowRightIcon
                                )}
                              </button>
                            }
                          />
                        </div>

                        {/* Privacy Policy */}
                        <div className="w-full">
                          <p className="text-[14px] leading-[20px] text-[#56554d] text-center">
                            {t('privacy.agreementPrefix')}{' '}
                            <a 
                              href="/privacy" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="underline decoration-solid md:hover:text-[#0d0c07] transition-colors"
                            >
                              {t('privacy.privacyPolicy')}
                            </a>.
                          </p>
                        </div>
                      </div>

                      {/* Actions Section */}
                      <div className="flex flex-col gap-5 items-center">
                        
                        {/* Open Email Button */}
                        {emailProvider && (
                          <button
                            onClick={() => openEmailClient(email)}
                            className="px-28 h-9 bg-[#EBEBEA;] rounded-[12px] border-[0.5px] border-[#EBEBEA] md:hover:bg-[#E2E1DF] md:hover:border-[#E2E1DF] transition-colors flex items-center justify-center"
                          >
                            <span className="text-[12px] font-semibold leading-[20px] text-[#0d0c07] text-center">
                              {t('verification.openEmail', { provider: emailProvider.name })}
                            </span>
                          </button>
                        )}

                        {/* Send another code */}
                        <div className="flex flex-col gap-1 items-center">
                          <button
                            onClick={handleResendCode}
                            disabled={resendCountdown > 0}
                            className={`
                              font-semibold text-[12.4px] leading-[20px] border-b-[1px] 
                              ${resendCountdown > 0 
                                ? 'text-[#4D4B42] border-[#65635D] text-[#72726e] cursor-default' 
                                : 'text-[#0D0C07] border-[#FFCB00] md:hover:border-[#0D0C07] cursor-pointer'
                              }
                            `}
                          >
                            {t('verification.sendAnotherCode')}
                          </button>
                          {resendCountdown > 0 && (
                            <span className="text-[12px] leading-[20px] text-[#56554d]">
                              {t('verification.inSeconds', { seconds: resendCountdown })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
